import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics } from '@capsicum/common';
import { FeedTypes, type ObjectDataShape, type PostTypes, useMe } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
	favouriteTeamFeedFiltersAtom,
	followedTeamsFeedFiltersAtom,
	suggestedTeamsFeedFiltersAtom
} from '../data-access';
import { type FilterTypeDimensionsType, type PostMainPartner, type PostSubTypes } from '../models';

const NO_VALUE = 'none';

type SendPostClickedEventProps = {
	feedType: FeedTypes;
	mainPartner: PostMainPartner;
	object: ObjectDataShape;
	verb: PostTypes;
};

type PostClickEventShape = {
	feed_type: FeedTypes;
	filter_type_dimensions: string;
	partner_id: string;
	post_sub_type: PostSubTypes | typeof NO_VALUE;
	post_type: PostTypes;
	user_VAT_country: string;
};

export const usePostAnalytics = () => {
	const analytics = useAnalytics();
	const { me } = useMe();
	const favouriteTeamFeedFilters = useAtomValue(favouriteTeamFeedFiltersAtom);
	const followedTeamsFeedFilters = useAtomValue(followedTeamsFeedFiltersAtom);
	const suggestedTeamsFeedFilters = useAtomValue(suggestedTeamsFeedFiltersAtom);

	const sendPostClickedEvent = useCallback(
		({ feedType, mainPartner, object, verb }: SendPostClickedEventProps) => {
			const filterTypeDimensionsMap: FilterTypeDimensionsType = {
				[FeedTypes.FAVOURITE_TEAM]: favouriteTeamFeedFilters.selectedFilters,
				[FeedTypes.FOLLOWED_TEAMS]: followedTeamsFeedFilters.selectedFilters,
				[FeedTypes.SUGGESTED_FEED]: suggestedTeamsFeedFilters.selectedFilters
			};

			const postSubType = object?.type !== verb ? object?.type : NO_VALUE;
			const getFilterTypeDimensions = () => {
				if (feedType in filterTypeDimensionsMap) {
					return filterTypeDimensionsMap[feedType as keyof FilterTypeDimensionsType].join(', ') || NO_VALUE;
				}

				return NO_VALUE;
			};

			const postData: PostClickEventShape = {
				feed_type: feedType,
				filter_type_dimensions: getFilterTypeDimensions(),
				partner_id: mainPartner.id,
				post_sub_type: postSubType || NO_VALUE,
				post_type: verb,
				user_VAT_country: me?.vatCountry || ''
			};

			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PostClicked, {
				feature_team: FeatureTeamEvent.ORFT,
				/** Data values have to be of string type, otherwise a warning from sendEvent will trigger. */
				...postData
			});
		},
		[analytics, followedTeamsFeedFilters.selectedFilters, me?.vatCountry]
	);

	return { sendPostClickedEvent };
};
